import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Scroll behaviour for the public marketing site.
 *
 * BrowserRouter has no <ScrollRestoration> (that needs a data router) and
 * MarketingLayout stays mounted across public routes, so without this the
 * window keeps whatever offset the last page left it at: open Product (which
 * scrolls the home page down to #product), click Pricing, and Pricing opens
 * halfway down.
 *
 * - New page (PUSH/REPLACE): start at the top.
 * - Same page, no hash (logo, or the link you are already on): glide to top.
 * - Hash (/#product): scroll to the section, clear of the floating nav.
 *   Smooth when it is already on screen, an instant jump when arriving from
 *   another page (gliding through the whole home page is slow, not delightful).
 * - Back/forward (POP): return to where the visitor was on that entry. The
 *   browser's own restoration runs before the lazy route chunk has rendered
 *   and lands short, so it is switched off while this layout is mounted.
 */

/** Clears the fixed nav pill (top-6 + 48px) with room to breathe. */
export const MARKETING_HEADER_OFFSET = 96;

/** How long to keep chasing a target while lazy chunks and images settle. */
const SETTLE_MS = 2000;
/** Frames the target must hold still before we stop chasing it early. */
const STABLE_FRAMES = 20;
const STORAGE_KEY = 'waypoint-marketing-scroll';
const USER_INPUT = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function readPositions(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

function writePositions(positions: Record<string, number>) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Private mode / blocked storage: back/forward still works in memory.
  }
}

export function hashTarget(hash: string): HTMLElement | null {
  if (!hash || hash === '#') return null;
  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    return null;
  }
}

export function sectionTop(el: HTMLElement): number {
  return Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY - MARKETING_HEADER_OFFSET));
}

/**
 * Keep the window at `resolve()` until the page stops moving. The target
 * section may not exist yet (route chunk still loading) and content above it
 * can grow after the first jump, so one scrollTo is not enough. Gives up as
 * soon as the visitor scrolls themselves. Returns a cancel function.
 */
function settle(resolve: () => number | null): () => void {
  const start = performance.now();
  let frame = 0;
  let stable = 0;
  let lastHeight = -1;
  let done = false;

  const stop = () => {
    if (done) return;
    done = true;
    cancelAnimationFrame(frame);
    for (const ev of USER_INPUT) window.removeEventListener(ev, stop);
  };
  for (const ev of USER_INPUT) window.addEventListener(ev, stop, { passive: true });

  const tick = () => {
    if (done) return;
    const y = resolve();
    const height = document.documentElement.scrollHeight;
    if (y !== null) {
      if (Math.abs(window.scrollY - y) > 1) window.scrollTo(0, y);
      stable = Math.abs(window.scrollY - y) <= 1 && height === lastHeight ? stable + 1 : 0;
    }
    lastHeight = height;
    if (stable >= STABLE_FRAMES || performance.now() - start > SETTLE_MS) stop();
    else frame = requestAnimationFrame(tick);
  };
  tick();
  return stop;
}

export function useMarketingScroll() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef<Record<string, number>>({});
  const loaded = useRef(false);
  const currentKey = useRef(location.key);
  const lastPathname = useRef<string | null>(null);

  useEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    const onScroll = () => {
      positions.current[currentKey.current] = window.scrollY;
    };
    const persist = () => writePositions(positions.current);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', persist);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', persist);
      persist();
      window.history.scrollRestoration = previous;
    };
  }, []);

  // Layout effect so the new page never paints at the old page's offset.
  useLayoutEffect(() => {
    const firstRun = !loaded.current;
    if (firstRun) {
      positions.current = readPositions();
      loaded.current = true;
      // Every fresh page load gets the router key "default", so that slot is
      // only ours to restore on a reload or back/forward, never on a new visit
      // (a link to /#product would otherwise reopen wherever the last one was).
      const entry = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (entry?.type === 'navigate') delete positions.current[location.key];
    }
    // The scroll listener has already recorded where we are leaving; reading
    // scrollY here would see it clamped to the new, shorter page.
    currentKey.current = location.key;
    const samePage = lastPathname.current === location.pathname;
    lastPathname.current = location.pathname;

    if (navigationType === 'POP') {
      // A hash typed into the address bar is a new entry with no router state,
      // so it also reads as "default": only the page load itself may use it.
      const saved = location.key === 'default' && !firstRun ? undefined : positions.current[location.key];
      if (saved !== undefined) return settle(() => saved);
      if (!location.hash) return;
    }

    const smooth = samePage && !prefersReducedMotion();

    if (location.hash) {
      const el = hashTarget(location.hash);
      const resolve = () => {
        const target = hashTarget(location.hash);
        return target ? sectionTop(target) : null;
      };
      if (el && smooth) {
        // Scroll-pinned sections above the target change the page's layout
        // as the glide passes them, so the first target can be off; settle
        // the last few pixels once the glide ends.
        window.scrollTo({ top: sectionTop(el), behavior: 'smooth' });
        let cancelSettle: (() => void) | undefined;
        const onEnd = () => {
          clearTimeout(fallback);
          window.removeEventListener('scrollend', onEnd);
          cancelSettle = settle(resolve);
        };
        // scrollend is missing in older Safari; the timeout covers it.
        const fallback = setTimeout(onEnd, 1200);
        window.addEventListener('scrollend', onEnd);
        return () => {
          clearTimeout(fallback);
          window.removeEventListener('scrollend', onEnd);
          cancelSettle?.();
        };
      }
      return settle(resolve);
    }

    if (smooth) window.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo(0, 0);
    // location.key changes on every navigation, including re-clicking the
    // link you are already on, which is what lets that glide back to the top.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
}
